/**
 * use-fit-height.ts — Cho một khung (vd ba cột của màn nhân viên AI) cao VỪA KHÍT phần còn lại của vùng hiển thị:
 * từ mép trên của khung tới đáy khung cuộn chứa nó (cửa sổ hoặc vùng nội dung của layout), trừ `bottomGap`.
 * Tính lại khi đổi kích thước cửa sổ hoặc khi phần phía trên khung đổi chiều cao (thanh trạng thái mở rộng…).
 * Màn hẹp hơn `minWidth` → null (để bố cục tự chảy, không ép chiều cao).
 */
import { useCallback, useLayoutEffect, useState } from 'react'

/** Phần tử cuộn gần nhất (overflow-y auto/scroll); không có → khung cuộn của trang. */
function scrollParentOf(el: HTMLElement): HTMLElement {
  let p = el.parentElement
  while (p && p !== document.body) {
    const oy = getComputedStyle(p).overflowY
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight >= p.clientHeight) return p
    p = p.parentElement
  }
  return document.scrollingElement as HTMLElement ?? document.documentElement
}

/** Chiều cao còn lại (thuần — có test): đáy vùng cuộn − đỉnh khung (theo toạ độ nội dung, không lệ thuộc vị trí cuộn). */
export function fitHeightFor(input: {
  elTop: number
  parentTop: number
  parentScrollTop: number
  parentClientHeight: number
  parentPaddingBottom: number
  bottomGap: number
  minHeight: number
}): number {
  const contentTop = input.elTop - input.parentTop + input.parentScrollTop
  const h = input.parentClientHeight - contentTop - input.parentPaddingBottom - input.bottomGap
  return Math.max(input.minHeight, Math.floor(h))
}

export function useFitHeight<T extends HTMLElement>(opts: { minWidth?: number; bottomGap?: number; minHeight?: number } = {}) {
  const { minWidth = 1200, bottomGap = 16, minHeight = 480 } = opts
  const [el, setEl] = useState<T | null>(null)
  const [height, setHeight] = useState<number | null>(null)
  const ref = useCallback((node: T | null) => setEl(node), [])

  useLayoutEffect(() => {
    if (!el) return
    const parent = scrollParentOf(el)
    const isPage = parent === document.scrollingElement || parent === document.documentElement
    const calc = () => {
      if (window.innerWidth < minWidth) { setHeight(null); return }
      const r = el.getBoundingClientRect()
      const next = fitHeightFor({
        elTop: r.top,
        parentTop: isPage ? 0 : parent.getBoundingClientRect().top,
        parentScrollTop: isPage ? window.scrollY : parent.scrollTop,
        parentClientHeight: isPage ? window.innerHeight : parent.clientHeight,
        parentPaddingBottom: isPage ? 0 : parseFloat(getComputedStyle(parent).paddingBottom) || 0,
        bottomGap,
        minHeight,
      })
      setHeight((prev) => (prev === next ? prev : next))
    }
    calc()
    window.addEventListener('resize', calc)
    window.visualViewport?.addEventListener('resize', calc)
    // Phần phía trên khung đổi cao (thanh trạng thái, đầu trang xuống dòng) → đỉnh khung dời → tính lại;
    // cả trang / vùng cuộn đổi kích thước (đổi cỡ cửa sổ, thu gọn menu) → tính lại.
    const ro = new ResizeObserver(calc)
    if (el.parentElement) ro.observe(el.parentElement)
    ro.observe(isPage ? document.documentElement : parent)
    return () => {
      window.removeEventListener('resize', calc)
      window.visualViewport?.removeEventListener('resize', calc)
      ro.disconnect()
    }
  }, [el, minWidth, bottomGap, minHeight])

  return { ref, height }
}
